import {
  planArchivedChecklistFieldReplay,
  planArchivedChecklistPartialReplay,
  type ArchivedChecklistPartial,
  type ArchivedPartialConflict,
  type ArchivedPartialReplayPlan,
  type ArchivedPartialValue,
} from '@/services/sync/ArchivedChecklistPartialReplay';

export type ReplayRow = Record<string, any>;
export interface ArchivedReplayLocalState {
  archive: ArchivedChecklistPartial & ReplayRow;
  conflict: ArchivedPartialConflict & ReplayRow;
  checklist: ReplayRow;
  values: Array<ArchivedPartialValue & ReplayRow>;
  pending: ReplayRow[];
  conflicts: ReplayRow[];
}
export interface ArchivedReplayRemoteState {
  checklist: ReplayRow | null;
  template: ReplayRow | null;
  values: ArchivedPartialValue[];
  fields: ReplayRow[];
  snapshotFields: ReplayRow[];
  tombstones: ReplayRow[];
  authorizedLegacyFieldIds: string[];
}
export interface ArchivedReplayCommit {
  companyId: string;
  local: ArchivedReplayLocalState;
  plan: ArchivedPartialReplayPlan;
  canCommit: () => boolean;
  now: number;
}
export interface ArchivedReplayDependencies {
  assertSession(): Promise<void>;
  isSessionCurrent(): boolean;
  readLocal(archiveId: string, companyId: string): Promise<ArchivedReplayLocalState | null>;
  readRemote(local: ArchivedReplayLocalState, companyId: string): Promise<ArchivedReplayRemoteState>;
  commit(input: ArchivedReplayCommit): Promise<boolean>;
  now(): number;
}

/** Reabre apenas respostas ausentes; equivalência completa encerra só o conflito de origem. */
export class ArchivedPartialReplayCoordinator {
  constructor(private readonly dependencies: ArchivedReplayDependencies) {}

  async process(archiveId: string, companyId: string): Promise<'queued' | 'complete' | 'preserved'> {
    const d = this.dependencies;
    await d.assertSession();
    const local = await d.readLocal(archiveId, companyId);
    if (!local || local.archive.empresa_id !== companyId || local.conflict.empresa_id !== companyId ||
      local.checklist.id !== local.archive.item_id || local.checklist.empresa_id !== companyId ||
      local.checklist.retired_at != null) return 'preserved';
    await d.assertSession();
    const remote = await d.readRemote(local, companyId);
    await d.assertSession();
    const parent = remote.checklist;
    if (!parent || parent.id !== local.archive.item_id || parent.empresa_id !== companyId ||
      parent.retired_at != null || parent.template_id !== local.checklist.template_id ||
      (parent.snapshot_id ?? null) !== (local.checklist.snapshot_id ?? null) ||
      !remote.template || remote.template.id !== parent.template_id ||
      remote.template.empresa_id !== companyId || remote.template.retired_at != null ||
      remote.values.some(row => row.empresa_id !== companyId) ||
      remote.tombstones.some(row => row.empresa_id !== companyId) ||
      remote.tombstones.some(row =>
        (row.entity_type === 'guincho_checklists' && row.entity_id === parent.id) ||
        (row.entity_type === 'guincho_templates_checklist' && row.entity_id === parent.template_id))) return 'preserved';

    const snapshotOriginalById = new Map<string, string>();
    for (const field of remote.snapshotFields) {
      if (field.empresa_id !== companyId || field.snapshot_id !== parent.snapshot_id ||
        !field.id || !field.original_campo_id || snapshotOriginalById.has(field.id)) return 'preserved';
      snapshotOriginalById.set(field.id, field.original_campo_id);
    }
    if (remote.values.some(row => local.values.some(value => value.id === row.id) &&
      (row.checklist_id !== parent.id || (row.snapshot_field_id && !snapshotOriginalById.has(row.snapshot_field_id))))) return 'preserved';
    const resolvable = new Set<string>();
    const terminal = new Set<string>();
    const authorized = new Set(remote.authorizedLegacyFieldIds);
    for (const field of remote.fields) {
      if (field.empresa_id !== companyId || field.template_id !== parent.template_id) return 'preserved';
      if (parent.snapshot_id ? [...snapshotOriginalById.values()].filter(id => id === field.id).length === 1
        : field.retired_at == null || authorized.has(field.id)) resolvable.add(field.id);
    }
    for (const tombstone of remote.tombstones) {
      if (tombstone.entity_type === 'guincho_campos_template' &&
        !(tombstone.deletion_kind === 'retire' && !parent.snapshot_id && authorized.has(tombstone.entity_id))) {
        terminal.add(tombstone.entity_id);
      }
      if (tombstone.entity_type === 'guincho_valores_checklist') {
        const metadata = typeof tombstone.metadata === 'string'
          ? JSON.parse(tombstone.metadata) : tombstone.metadata;
        for (const value of local.values) {
          if (tombstone.entity_id === value.id || (tombstone.parent_id === parent.id &&
            (metadata?.campo_id === value.campo_id ||
              (value.snapshot_field_id && metadata?.snapshot_field_id === value.snapshot_field_id)))) {
            const fieldId = value.campo_id ?? snapshotOriginalById.get(value.snapshot_field_id ?? '');
            if (fieldId) terminal.add(fieldId);
          }
        }
      }
    }
    // URI local de mídia exige a recuperação/upload específico antes de qualquer replay de valor.
    for (const value of local.values) {
      const fieldId = value.campo_id ?? snapshotOriginalById.get(value.snapshot_field_id ?? '');
      const definition = remote.snapshotFields.find(row => row.id === value.snapshot_field_id) ??
        remote.fields.find(row => row.id === fieldId);
      const fieldType = typeof definition?.tipo === 'string'
        ? definition.tipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';
      if (fieldId && ['foto', 'photo', 'assinatura', 'signature', 'marcacao', 'mark', 'marcacao_imagem'].includes(fieldType) &&
        typeof value.valor === 'string' && value.valor.length > 0 && !/^https?:\/\//i.test(value.valor)) terminal.add(fieldId);
    }
    for (const fieldId of terminal) resolvable.delete(fieldId);
    for (const value of local.values) {
      const fieldId = value.campo_id ?? snapshotOriginalById.get(value.snapshot_field_id ?? '');
      if (fieldId && !resolvable.has(fieldId)) terminal.add(fieldId);
    }
    const base = { companyId, archive: local.archive, conflict: local.conflict,
      localValues: local.values, remoteValues: remote.values.filter(row => {
        const fieldId = row.campo_id ?? snapshotOriginalById.get(row.snapshot_field_id ?? '');
        return !fieldId || !terminal.has(fieldId);
      }) };
    const plan = local.archive.reason === 'field_reference_not_resolved'
      ? planArchivedChecklistFieldReplay({ ...base, resolvableFieldIds: resolvable })
      : planArchivedChecklistPartialReplay({ ...base, terminalFieldIds: terminal, snapshotOriginalById });
    if (!plan.replayId || !['ready', 'complete'].includes(plan.status)) return 'preserved';
    // Uma nova tentativa que já materializou conflito exige nova análise, sem loop automático.
    if (local.conflicts.some(row => row.pending_operation_id === plan.replayId &&
      ['open', 'blocked'].includes(row.status))) return 'preserved';
    if (local.pending.some(row => row.id !== plan.replayId)) return 'preserved';
    if (plan.status === 'complete') {
      // Readback final reduz a janela remota; o CAS abaixo protege as alterações locais.
      // Uma edição remota posterior continua sendo uma nova versão a receber pelo pull.
      const finalRemote = await d.readRemote(local, companyId);
      await d.assertSession();
      if (canonical(finalRemote) !== canonical(remote)) return 'preserved';
    }
    await d.assertSession();
    if (!d.isSessionCurrent() || !await d.commit({ companyId, local, plan,
      canCommit: () => d.isSessionCurrent(), now: d.now() })) return 'preserved';
    return plan.status === 'complete' ? 'complete' : 'queued';
  }
}

export interface ArchivedReplayTransaction {
  get(table: string, id: string): Promise<ReplayRow | null | undefined>;
  list(table: string, companyId: string, checklistId: string): Promise<ReplayRow[]>;
  insert(table: string, row: ReplayRow): Promise<void>;
  update(table: string, id: string, changes: ReplayRow): Promise<void>;
  remove(table: string, id: string): Promise<void>;
}
const canonical = (value: any): string => JSON.stringify(value, (_key, item) =>
  item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
const sameRows = (a: ReplayRow[], b: ReplayRow[]): boolean =>
  canonical([...a].sort((x, y) => String(x.id).localeCompare(String(y.id)))) ===
  canonical([...b].sort((x, y) => String(x.id).localeCompare(String(y.id))));

/** Deve ser executada integralmente dentro da transação SQLite/Dexie do adapter. */
export const commitArchivedPartialReplay = async (
  input: ArchivedReplayCommit,
  tx: ArchivedReplayTransaction
): Promise<boolean> => {
  const { local, companyId, plan } = input;
  if (!input.canCommit() || !plan.replayId || !/^archived-replay:[0-9a-f]{40}$/.test(plan.replayId) ||
    !['ready', 'complete'].includes(plan.status) || local.archive.empresa_id !== companyId ||
    local.conflict.empresa_id !== companyId || local.checklist.empresa_id !== companyId ||
    local.checklist.id !== local.archive.item_id) return false;
  const archive = await tx.get('accounted_pending_operations', local.archive.id);
  const checklist = await tx.get('guincho_checklists', local.archive.item_id);
  const values = await tx.list('guincho_valores_checklist', companyId, local.archive.item_id);
  const pending = await tx.list('pending_operations', companyId, local.archive.item_id);
  const conflicts = await tx.list('sync_conflicts', companyId, local.archive.item_id);
  if (canonical(archive) !== canonical(local.archive) || canonical(checklist) !== canonical(local.checklist) ||
    !sameRows(values, local.values) || !sameRows(pending, local.pending) ||
    !sameRows(conflicts, local.conflicts) || !input.canCommit()) return false;
  if (!conflicts.some(row => row.id === local.conflict.id && canonical(row) === canonical(local.conflict)) ||
    pending.some(row => row.id !== plan.replayId)) return false;
  const queued = pending.find(row => row.id === plan.replayId);
  if (plan.status === 'ready') {
    if (!plan.pending || !matchesArchivedReplayPending(plan.pending, local.archive, plan.replayId) ||
      (queued && Object.entries(plan.pending).some(([key, value]) => queued[key] !== value))) return false;
    if (!queued) {
      const collision = await tx.get('pending_operations', plan.replayId);
      if (collision) return false;
      await tx.insert('pending_operations', plan.pending);
    }
  } else {
    // Só remove uma tentativa observada antes da leitura remota e ainda idêntica no CAS.
    if (queued) {
      if (!matchesArchivedReplayPending(queued, local.archive, plan.replayId)) return false;
      await tx.remove('pending_operations', queued.id);
    }
    await tx.update('sync_conflicts', local.conflict.id,
      { status: 'resolved', resolved_at: input.now, updated_at: input.now });
  }
  if (!input.canCommit()) throw new Error('session_changed_before_archived_replay_commit');
  return true;
};

const matchesArchivedReplayPending = (row: ReplayRow, archive: ArchivedChecklistPartial, replayId: string): boolean => {
  if (row.id !== replayId || row.operation !== archive.operation || row.collection !== archive.collection ||
    row.item_id !== archive.item_id || row.empresa_id !== archive.empresa_id || row.user_id !== archive.user_id ||
    row.timestamp !== archive.original_timestamp) return false;
  try {
    const stored = JSON.parse(archive.data ?? 'null');
    const answer = Object.prototype.hasOwnProperty.call(stored?.payload ?? {}, 'observacao')
      ? { valor: stored.payload.valor, observacao: stored.payload.observacao } : stored?.payload?.valor;
    const answers = archive.reason === 'field_reference_not_resolved'
      ? { [stored.field_id]: answer } : stored;
    const queued = JSON.parse(row.data);
    return queued && typeof queued === 'object' && !Array.isArray(queued) && Object.keys(queued).length > 0 &&
      Object.entries(queued).every(([field, value]) => Object.prototype.hasOwnProperty.call(answers, field) &&
        canonical(answers[field]) === canonical(value));
  } catch { return false; }
};
